import { Component, inject, OnInit } from '@angular/core';

import { ErrorService } from '@shared/services/error.service';
import { CombosService } from '@shared/services/combos.service';
import { Zona } from '@shared/services/interfaces/combos.interface';
import { Badge } from '@shared/components/ui/badge/badge';
import { PageHeaderComponent } from '@shared/components/common/page-header/page-header.component';
import {
  DataTable,
  FilterOption,
  FilterSelect,
  FiltersContainer,
  Pagination,
  SearchInput,
  TableCellDirective,
  TableColumn
} from '@shared/components/ui/list-components';
import { EstablecimientoUbicacion } from './establecimientos.interface';
import { EstablecimientosUbicacionService } from './establecimientos.service';

type BadgeColor = 'primary' | 'success' | 'error' | 'warning' | 'info' | 'light' | 'dark';
type BadgeVariant = 'light' | 'solid';

@Component({
  selector: 'app-ubicacion-establecimientos',
  imports: [
    Badge,
    DataTable,
    FilterSelect,
    FiltersContainer,
    PageHeaderComponent,
    Pagination,
    SearchInput,
    TableCellDirective,
  ],
  templateUrl: './establecimientos.html'
})
export class EstablecimientosUbicacion implements OnInit {
  private establecimientosService = inject(EstablecimientosUbicacionService);
  private combosService = inject(CombosService);
  private errorService = inject(ErrorService);

  establecimientos: EstablecimientoUbicacion[] = [];
  zonas: Zona[] = [];
  loading = false;

  currentPage = 1;
  lastPage = 1;
  perPage = 15;
  total = 0;

  searchTerm = '';
  selectedObjetivo: string | number = '';
  selectedZona: string | number = '';

  columns: TableColumn[] = [
    { key: 'nombre', header: 'Establecimiento' },
    { key: 'cliente_nombre', header: 'Cliente' },
    { key: 'objetivo', header: 'Objetivo' },
    { key: 'direccion', header: 'Dirección' },
    { key: 'zona', header: 'Zona' },
    { key: 'km', header: 'Kilómetro' },
  ];

  objetivosOptions: FilterOption[] = [
    { value: 'E', label: 'Producción de embriones' },
    { value: 'P', label: 'Preñez' },
    { value: 'X', label: 'Producción de Embriones/Preñez' },
  ];

  get zonasOptions(): FilterOption[] {
    return this.zonas.map(zona => ({
      value: zona.id,
      label: zona.direccion_completa || zona.nombre,
    }));
  }

  private readonly objetivosMap: Record<string, { label: string; color: BadgeColor; variant?: BadgeVariant }> = {
    E: { label: 'Producción de embriones', color: 'primary' },
    P: { label: 'Preñez', color: 'success' },
    X: { label: 'Producción de Embriones/Preñez', color: 'info' },
  };

  ngOnInit(): void {
    this.getZonas();
    this.getEstablecimientos();
  }

  getEstablecimientos(page: number = 1): void {
    this.loading = true;

    const filters = {
      page,
      per_page: this.perPage,
      search: this.searchTerm || undefined,
      objetivo: this.selectedObjetivo ? this.selectedObjetivo.toString() : undefined,
      ubi_zona_id: this.selectedZona ? Number(this.selectedZona) : undefined,
    };

    this.establecimientosService.index(filters).subscribe({
      next: (response) => {
        if (response.status) {
          this.establecimientos = response.data.data;
          this.currentPage = response.data.pagination.current_page;
          this.lastPage = response.data.pagination.last_page;
          this.total = response.data.pagination.total;
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.loading = false;
    });
  }

  onSearchChange(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.currentPage = 1;
    this.getEstablecimientos();
  }

  onObjetivoChange(value: string | number): void {
    this.selectedObjetivo = value;
    this.currentPage = 1;
    this.getEstablecimientos();
  }

  onZonaChange(value: string | number): void {
    this.selectedZona = value;
    this.currentPage = 1;
    this.getEstablecimientos();
  }

  get hasActiveFilters(): boolean {
    return !!this.searchTerm || !!this.selectedObjetivo || !!this.selectedZona;
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.selectedObjetivo = '';
    this.selectedZona = '';
    this.currentPage = 1;
    this.getEstablecimientos();
  }

  onPageChange(page: number): void {
    this.getEstablecimientos(page);
  }

  onPerPageChange(perPage: number): void {
    this.perPage = perPage;
    this.currentPage = 1;
    this.getEstablecimientos();
  }

  getObjetivoBadge(objetivo: string | null | undefined): { label: string; color: BadgeColor; variant?: BadgeVariant } {
    return this.objetivosMap[objetivo ?? ''] ?? { label: 'Sin objetivo', color: 'light', variant: 'light' };
  }

  private getZonas(): void {
    this.combosService.getAllZonasCombo().subscribe({
      next: (response) => {
        if (response.status) {
          this.zonas = response.data;
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }
}
